import React, { useEffect, useMemo, useRef, useState } from "react";
import { DespatchReceiver, ShipmentDetail, ShipmentStatus as S } from "../types";
import { shippingService } from "../services/dataService";
import { usePermissions } from "../context/PermissionContext";
import { toDateTimeInput, todayLocal } from "../utils/dates";
import { despatchError, driverTcknValid, normalizePlate, plateValid, postalValid, shDriverNames, shErr, shFmtQty, taxIdValid } from "../utils/shipping";
import "../pages/PurchaseOrders.css";
import "../pages/Shipping.css";

export type ShipmentAction = "ship" | "deliver" | "cancel" | "reissue";

interface Props {
  shipment: ShipmentDetail;
  action: ShipmentAction;
  /** Sistem ayarı: e-İrsaliye açık mı (sevkte irsaliye platformda düzenlenir) */
  eDespatch?: boolean;
  /** Matbu irsaliye yedeği açık mı (e-İrsaliye düzenlenemezse kâğıt irsaliyeyle sevk) */
  paperFallback?: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
}

const nowLocal = () => { const d = new Date(); d.setSeconds(0, 0); return toDateTimeInput(d); };
const today = () => todayLocal();
const TITLE: Record<ShipmentAction, string> = { ship: "Sevk et", deliver: "Teslim edildi", cancel: "Sevkiyatı iptal et", reissue: "Yeni e-İrsaliye düzenle" };
const BUSY: Record<ShipmentAction, string> = { ship: "Sevk ediliyor", deliver: "Kaydediliyor", cancel: "İptal ediliyor", reissue: "Düzenleniyor" };
const HINT: Record<ShipmentAction, string> = {
  ship: "Sevk zamanı belge tarihi olur; resmi irsaliye numarası siparişlerin faturasında referans olarak kullanılır.",
  deliver: "Teslim alan kişi ve zaman kaydedilir; teslim edilmiş sevkiyat iptal edilemez.",
  cancel: "İptal edilen sevkiyat yeniden açılamaz. Sebep sevkiyat kartında görünür.",
  reissue: "GİB'in reddettiği e-İrsaliyenin yerine düzeltilmiş bilgilerle yeni e-İrsaliye düzenlenir (yeni numara). Stok yeniden düşülmez.",
};
const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
};

/** e-İrsaliye alanları (sunucu ShipDto ile aynı adlar); boş gönderilen alan kayıtlı değeri korur. */
interface Transport {
  vehiclePlate: string; plateIsForeign: boolean; trailerPlate: string;
  driverFirstName: string; driverLastName: string; driverTckn: string;
  carrier: string; carrierTaxNumber: string; deliveryPostalCode: string; receiverAlias: string;
}
const transportOf = (s: ShipmentDetail): Transport => {
  const [first, last] = shDriverNames(s);
  return {
    vehiclePlate: s.vehiclePlate || "", plateIsForeign: !!s.plateIsForeign, trailerPlate: s.trailerPlate || "",
    driverFirstName: first, driverLastName: last, driverTckn: s.driverTckn || "",
    carrier: s.carrier || "", carrierTaxNumber: s.carrierTaxNumber || "", deliveryPostalCode: s.deliveryPostalCode || "", receiverAlias: "",
  };
};
type FieldErrors = Partial<Record<"vehiclePlate" | "trailerPlate" | "driver" | "driverTckn" | "carrierTaxNumber" | "deliveryPostalCode" | "receiverAlias" | "printedWaybill", string>>;

/** e-İrsaliye alan doğrulaması — sunucu ön kontrolüyle (DespatchPreValidator) aynı kurallar. */
const transportErrors = (t: Transport, aliasRequired: boolean): FieldErrors => {
  const e: FieldErrors = {};
  if (!t.vehiclePlate.trim()) e.vehiclePlate = "Plaka zorunlu (GİB her e-İrsaliyede plaka ister; kargoda da).";
  else if (!plateValid(t.vehiclePlate, t.plateIsForeign)) e.vehiclePlate = t.plateIsForeign ? "Yabancı plaka yalnız harf, rakam, tire içerebilir." : "Plaka 01–81 il koduyla başlamalı (ör. 34ABC123); yabancı plakaysa işaretleyin.";
  if (t.trailerPlate.trim() && !plateValid(t.trailerPlate, t.plateIsForeign)) e.trailerPlate = "Dorse plakası geçersiz.";
  const anyDriver = !!(t.driverFirstName.trim() || t.driverLastName.trim() || t.driverTckn.trim());
  const carrier = t.carrierTaxNumber.trim();
  if (anyDriver) {
    if (!t.driverFirstName.trim() || !t.driverLastName.trim()) e.driver = "Şoförün adı ve soyadı ayrı ayrı girilmeli.";
    if (!t.driverTckn.trim()) e.driverTckn = "Şoför TCKN zorunlu.";
    else if (!driverTcknValid(t.driverTckn.trim())) e.driverTckn = "Şoför TCKN geçersiz (11111111111 kullanılamaz).";
  }
  if (carrier && !taxIdValid(carrier)) e.carrierTaxNumber = "Taşıyıcı VKN/TCKN geçersiz.";
  if (!anyDriver && !carrier) e.driver = "Şoför (ad, soyad, TCKN) ya da taşıyıcı VKN'si girilmeli.";
  if (!postalValid(t.deliveryPostalCode)) e.deliveryPostalCode = t.deliveryPostalCode.trim() ? "Posta kodu 5 hane ve 01–81 il koduyla başlamalı." : "Teslimat posta kodu zorunlu (ör. 34200).";
  if (aliasRequired && !t.receiverAlias) e.receiverAlias = "Alıcının birden çok e-İrsaliye etiketi var; birini seçin.";
  return e;
};

/** Sevkiyat işlemleri: Sevk et (e-İrsaliye açıkken irsaliyeyle), Teslim edildi, İptal, Yeni e-İrsaliye. Tek modal, işlem türüne göre alanlar değişir. */
const ShipmentActionModal: React.FC<Props> = ({ shipment, action, eDespatch, paperFallback, onClose, onDone }) => {
  const { hasModule } = usePermissions();
  const despatchMode = (action === "ship" && !!eDespatch) || action === "reissue";
  const locked = action === "ship" && !!shipment.despatchLocked;
  const [when, setWhen] = useState(nowLocal());
  // e-İrsaliye: dokunulmayan sevk zamanı gönderim anında "şimdi" olur (pencere uzun açık kalırsa geçmişe düşmesin)
  const [whenTouched, setWhenTouched] = useState(false);
  const [waybillNo, setWaybillNo] = useState(shipment.waybillNo || "");
  const [waybillDate, setWaybillDate] = useState(shipment.waybillDate ? shipment.waybillDate.slice(0, 10) : today());
  const [receivedBy, setReceivedBy] = useState("");
  const [reason, setReason] = useState("");
  const [t, setT] = useState<Transport>(() => transportOf(shipment));
  const [receiver, setReceiver] = useState<DespatchReceiver | null>(null);
  const [receiverLoading, setReceiverLoading] = useState(despatchMode);
  const [tried, setTried] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const [outcome, setOutcome] = useState<"" | "Rejected" | "Failed" | "Unknown">("");
  const [paperOpen, setPaperOpen] = useState(false);
  const [printedNo, setPrintedNo] = useState("");
  const [printedDate, setPrintedDate] = useState(today());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Açan öğe ilk render'da (odak modala geçmeden) yakalanır
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const firstRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const setFirst = (el: HTMLInputElement | HTMLTextAreaElement | null) => { firstRef.current = el; };

  const n = shipment.lines.length;
  const aliases = receiver?.available && receiver.registered ? receiver.aliases : [];
  const aliasRequired = aliases.length > 1;
  const fieldErrors = useMemo<FieldErrors>(() => (despatchMode ? { ...transportErrors(t, aliasRequired), ...serverErrors } : {}), [despatchMode, t, aliasRequired, serverErrors]);
  const despatchValid = Object.keys(transportErrors(t, aliasRequired)).length === 0;
  const valid = action === "ship" ? (despatchMode ? despatchValid : true) : action === "deliver" ? !!receivedBy.trim() : action === "cancel" ? reason.trim().length >= 3 : despatchValid;
  const show = (k: keyof FieldErrors) => (tried || !!serverErrors[k]) && fieldErrors[k];

  // Alıcı: e-İrsaliye mükellefi mi, PK etiketleri (birden çoksa seçilir; seçim firma kartına yazılır)
  useEffect(() => {
    if (!despatchMode) return;
    let live = true;
    setReceiverLoading(true);
    shippingService.despatchReceiver(shipment.firmId)
      .then((r) => {
        if (!live) return;
        setReceiver(r);
        const pick = r.selectedAlias && r.aliases.includes(r.selectedAlias) ? r.selectedAlias : r.aliases.length === 1 ? r.aliases[0] : "";
        setT((x) => ({ ...x, receiverAlias: x.receiverAlias || (r.aliases.length > 1 ? pick : "") }));
      })
      .catch(() => { if (live) setReceiver({ available: false, registered: false, aliases: [], message: "Alıcı durumu sorgulanamadı; platform irsaliyeyi gönderirken yeniden sorar." }); })
      .finally(() => { if (live) setReceiverLoading(false); });
    return () => { live = false; };
  }, [despatchMode, shipment.firmId]);

  const setTf = <K extends keyof Transport>(k: K, v: Transport[K]) => { setT((x) => ({ ...x, [k]: v })); setServerErrors({}); setError(""); };

  const transportDto = () => ({
    vehiclePlate: normalizePlate(t.vehiclePlate) || null, plateIsForeign: t.plateIsForeign, trailerPlate: normalizePlate(t.trailerPlate) || null,
    driverFirstName: t.driverFirstName.trim() || null, driverLastName: t.driverLastName.trim() || null, driverTckn: t.driverTckn.trim() || null,
    carrier: t.carrier.trim() || null, carrierTaxNumber: t.carrierTaxNumber.trim() || null,
    deliveryPostalCode: t.deliveryPostalCode.trim() || null, receiverAlias: t.receiverAlias || null,
  });

  /** Sunucunun e-İrsaliye hatası: alan vurgusu + sonuç türü (belirsizse aynı düğme aynı irsaliyeyle yeniden dener). */
  const takeDespatchError = (err: any) => {
    if (!err?.response) {
      // Yanıt gelmedi (zaman aşımı / bağlantı): sunucu işlemi bitirmiş olabilir; tekrar deneme aynı irsaliyeyi kullanır
      setError("Sunucudan yanıt alınamadı; sonuç belirsiz. Pencereyi kapatıp sevkiyatın durumuna bakın; hâlâ taslaksa yeniden deneyin.");
      setOutcome("Unknown"); setServerErrors({});
      return;
    }
    const d = despatchError(err);
    if (!d) { setError(shErr(err, "İşlem yapılamadı.")); setOutcome(""); return; }
    setError(d.detail || d.message || "e-İrsaliye düzenlenemedi.");
    setOutcome(d.code === "DESPATCH_PRECHECK" ? "" : (d.outcome || ""));
    const fe: FieldErrors = {};
    (d.errors || []).forEach((x) => {
      const f = (x.field || "").toLowerCase();
      if (f.includes("vehicle") || f === "vehicleplate") fe.vehiclePlate = x.message;
      else if (f.includes("trailer")) fe.trailerPlate = x.message;
      else if (f.includes("tckn")) fe.driverTckn = x.message;
      else if (f.includes("driver")) fe.driver = x.message;
      else if (f.includes("carrier")) fe.carrierTaxNumber = x.message;
      else if (f.includes("postal")) fe.deliveryPostalCode = x.message;
      else if (f.includes("alias")) fe.receiverAlias = x.message;
      else if (f.includes("printed")) fe.printedWaybill = x.message;
    });
    setServerErrors(fe);
  };

  const run = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (saving) return;
    setError(""); setTried(true);
    if (action === "cancel" && reason.trim().length < 3) return setError("İptal sebebi en az 3 karakter olmalı.");
    if (action === "deliver" && !receivedBy.trim()) return setError("Teslim alan kişiyi yazın.");
    if ((action === "ship" || action === "deliver") && !when) return setError(action === "ship" ? "Sevk zamanını girin." : "Teslim zamanını girin.");
    if (despatchMode && !despatchValid) return setError("e-İrsaliye için eksik ya da hatalı alanlar var; işaretli alanları düzeltin.");
    setSaving(true);
    try {
      if (action === "ship" && despatchMode) {
        const r = await shippingService.ship(shipment.shipmentId, { shippedAt: `${whenTouched ? when : nowLocal()}:00`, ...transportDto() });
        onDone(`e-İrsaliye ${r.despatchNumber ?? ""} düzenlendi; sevk edildi.${r.stockMovements > 0 ? ` ${r.stockMovements} stok çıkışı işlendi.` : ""}`);
      } else if (action === "ship") {
        const r = await shippingService.ship(shipment.shipmentId, {
          shippedAt: `${when}:00`, // yerel duvar saati, "Z"siz: sunucu TR saati tutar, toISOString 3 saat geri yazar
          waybillNo: waybillNo.trim() || null,
          waybillDate: waybillNo.trim() && waybillDate ? waybillDate : null, // tarih boşsa sunucu sevk tarihini yazar
        });
        onDone(r.stockMovements > 0 ? `Sevk edildi; ${r.stockMovements} stok çıkışı işlendi.` : "Sevk edildi.");
      } else if (action === "reissue") {
        const r = await shippingService.despatchReissue(shipment.shipmentId, transportDto());
        onDone(`Yeni e-İrsaliye ${r.despatchNumber ?? ""} düzenlendi. Araçtaki nüshayı yenisiyle değiştirin.`);
      } else if (action === "deliver") {
        await shippingService.deliver(shipment.shipmentId, { deliveredAt: `${when}:00`, receivedBy: receivedBy.trim() });
        onDone("Teslimat onaylandı.");
      } else {
        await shippingService.cancel(shipment.shipmentId, reason.trim());
        onDone("Sevkiyat iptal edildi.");
      }
      onClose();
    } catch (err: any) {
      if (despatchMode) takeDespatchError(err);
      else setError(shErr(err, "İşlem yapılamadı."));
    } finally { setSaving(false); }
  };

  /** Matbu yedek: e-İrsaliye düzenlenemediğinde (gönderilemedi / belirsiz) kâğıt irsaliyeyle sevk; ertesi gün MATBUDAN e-İrsaliyeye dönüştürülür. */
  const runPaper = async () => {
    if (saving) return;
    setTried(true);
    if (!printedNo.trim()) { setServerErrors({ printedWaybill: "Matbu irsaliyenin seri-sıra numarasını yazın." }); return; }
    if (!despatchValid) return setError("Matbu irsaliye de sonradan e-İrsaliyeye dönüştürülür: işaretli alanları tamamlayın.");
    setSaving(true); setError("");
    try {
      await shippingService.shipPaper(shipment.shipmentId, { shippedAt: `${whenTouched ? when : nowLocal()}:00`, printedWaybillNo: printedNo.trim(), printedWaybillDate: printedDate, ...transportDto() });
      onDone(`Matbu irsaliye ${printedNo.trim()} ile sevk edildi. En geç yarın sevkiyat kartından "e-İrsaliyeye dönüştür" ile MATBUDAN e-İrsaliye düzenleyin.`);
      onClose();
    } catch (err: any) { takeDespatchError(err); }
    finally { setSaving(false); }
  };

  // Ctrl+Enter her zaman güncel formla gönderir (kapanış değişkenlerini tek tek bağımlılığa yazmak yerine)
  const runRef = useRef(run);
  runRef.current = run;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) { e.stopPropagation(); onClose(); }
      else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); runRef.current(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose, saving]);

  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);
  // autoFocus yerine efekt: StrictMode geliştirmede yukarıdaki geri-odak temizliğini bir kez erken çalıştırır, autoFocus ezilirdi
  useEffect(() => { firstRef.current?.focus(); }, []);

  const canPaper = action === "ship" && despatchMode && !!paperFallback && (outcome === "Failed" || outcome === "Unknown");
  const ro = locked; // belirsiz deneme: içerik değişmez, aynı irsaliye yeniden denenir

  return (
    <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <form className={`po-modal${despatchMode ? " wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby="sh-act-title" onSubmit={run}>
        <div className="po-modal-head">
          <div><span className="po-kicker">{shipment.number} · {shipment.firmName}</span><h3 id="sh-act-title">{TITLE[action]}{action === "ship" && despatchMode ? " · e-İrsaliye" : ""}</h3></div>
          <button type="button" className="po-ib big" aria-label="Kapat" onClick={onClose} disabled={saving}>{I.close}</button>
        </div>

        <div className="po-modal-body">
          <p className="po-form-hint">{action === "ship" && despatchMode
            ? "Sevk ederken e-İrsaliye düzenlenir. İrsaliye entegratöre ulaşmadan sevkiyat \"Sevk edildi\"ye geçmez ve stok düşülmez; numarayı sistem verir."
            : HINT[action]}</p>
          {error && (
            <div className={`po-note ${outcome === "Unknown" ? "warn" : "err"}`} role="alert">
              {error}
              {outcome === "Unknown" && <div className="sh-ed-retry">Aynı düğmeyle yeniden deneyin; aynı irsaliye kullanılır, çift irsaliye oluşmaz.</div>}
            </div>
          )}
          {locked && <div className="po-note warn">Son e-İrsaliye denemesinin sonucu henüz belli değil. Bilgiler değiştirilemez; "Sevk et" aynı irsaliyeyle yeniden dener ve sonucu netleştirir.</div>}

          {action === "ship" && !despatchMode && <>
            <div className="po-grid2">
              <label className="po-field"><span>Sevk zamanı</span><input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></label>
              <label className="po-field"><span>Resmi irsaliye no<em>isteğe bağlı</em></span><input value={waybillNo} maxLength={32} ref={setFirst} placeholder="Kağıt / portal irsaliye no" onChange={(e) => setWaybillNo(e.target.value)} />
                {shipment.waybillNo && !waybillNo.trim() && <span className="po-fieldnote warn">Boş bırakılırsa kayıtlı no ({shipment.waybillNo}) korunur; silmek için taslağı düzenleyin.</span>}</label>
              <label className="po-field"><span>İrsaliye tarihi</span><input type="date" value={waybillDate} disabled={!waybillNo.trim()} onChange={(e) => setWaybillDate(e.target.value)} /></label>
            </div>
            <div className="po-note info">
              {hasModule("stock")
                ? `${n} satır sevk edilecek; üretim stoğu tanımlı modeller için stoktan çıkış işlenir.`
                : `${n} satır sevk edilecek. Stok modülü lisansta olmadığı için stok bakiyesi değişmez.`}
            </div>
          </>}

          {despatchMode && <>
            {action === "ship" && (
              <div className="po-grid2">
                <label className="po-field"><span>Fiili sevk zamanı<em>geçmiş seçilemez</em></span>
                  <input type="datetime-local" value={when} min={nowLocal()} disabled={ro} onChange={(e) => { setWhen(e.target.value); setWhenTouched(true); }} />
                  <span className="po-fieldnote">{whenTouched ? "e-İrsaliye malın yola çıkmasından önce düzenlenir." : "Değiştirmezseniz gönderim anı yazılır."}</span>
                </label>
                <div className="po-field"><span>Alıcı</span>
                  <div className={`sh-ed-recv ${receiverLoading ? "" : receiver?.available ? (receiver.registered ? "ok" : "info") : "warn"}`}>
                    {receiverLoading ? <><span className="po-spin" /> Sorgulanıyor</>
                      : !receiver?.available ? (receiver?.message || "Sorgulanamadı.")
                      : receiver.registered ? <>e-İrsaliye mükellefi · İrsaliye Yanıtı 7 gün içinde beklenir</>
                      : <span title={receiver.fallbackNote || undefined}>e-İrsaliye kayıtlı değil · GİB sanal alıcısına gider, yanıt beklenmez</span>}
                  </div>
                </div>
              </div>
            )}
            {aliasRequired && (
              <label className="po-field"><span>Alıcının e-İrsaliye etiketi<em>zorunlu</em></span>
                <select className={`po-select ${show("receiverAlias") ? "is-invalid" : ""}`} value={t.receiverAlias} disabled={ro} onChange={(e) => setTf("receiverAlias", e.target.value)}>
                  <option value="">Etiket seçin</option>
                  {aliases.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                {show("receiverAlias") ? <span className="po-fielderr">{fieldErrors.receiverAlias}</span> : <span className="po-fieldnote">Seçim firma kartına kaydedilir.</span>}
              </label>
            )}

            <div className="po-form-sec"><h4>Araç ve şoför</h4>
              <div className="po-grid4">
                <label className="po-field"><span>Plaka<em>zorunlu</em></span>
                  <input ref={setFirst} value={t.vehiclePlate} maxLength={20} placeholder="34ABC123" disabled={ro} className={show("vehiclePlate") ? "is-invalid" : ""} onChange={(e) => setTf("vehiclePlate", e.target.value.toUpperCase())} />
                  {show("vehiclePlate") && <span className="po-fielderr">{fieldErrors.vehiclePlate}</span>}
                </label>
                <label className="po-field"><span>Dorse plakası<em>isteğe bağlı</em></span>
                  <input value={t.trailerPlate} maxLength={20} disabled={ro} className={show("trailerPlate") ? "is-invalid" : ""} onChange={(e) => setTf("trailerPlate", e.target.value.toUpperCase())} />
                  {show("trailerPlate") && <span className="po-fielderr">{fieldErrors.trailerPlate}</span>}
                </label>
                <label className="po-field sh-ed-check span2"><span>Plaka türü</span>
                  <span className="sh-ed-checkrow"><input type="checkbox" checked={t.plateIsForeign} disabled={ro} onChange={(e) => setTf("plateIsForeign", e.target.checked)} /> Yabancı plakalı araç / dorse</span>
                </label>
                <label className="po-field"><span>Şoför adı</span>
                  <input value={t.driverFirstName} maxLength={50} disabled={ro} className={show("driver") ? "is-invalid" : ""} onChange={(e) => setTf("driverFirstName", e.target.value)} />
                </label>
                <label className="po-field"><span>Şoför soyadı</span>
                  <input value={t.driverLastName} maxLength={50} disabled={ro} className={show("driver") ? "is-invalid" : ""} onChange={(e) => setTf("driverLastName", e.target.value)} />
                </label>
                <label className="po-field span2"><span>Şoför TCKN<em>11 hane</em></span>
                  <input value={t.driverTckn} maxLength={11} inputMode="numeric" disabled={ro} className={show("driverTckn") ? "is-invalid" : ""} onChange={(e) => setTf("driverTckn", e.target.value.replace(/\D/g, ""))} />
                  {show("driverTckn") && <span className="po-fielderr">{fieldErrors.driverTckn}</span>}
                </label>
                <label className="po-field span2"><span>Nakliyeci / kargo</span>
                  <input value={t.carrier} maxLength={100} disabled={ro} onChange={(e) => setTf("carrier", e.target.value)} />
                </label>
                <label className="po-field span2"><span>Taşıyıcı VKN / TCKN<em>şoför yoksa zorunlu</em></span>
                  <input value={t.carrierTaxNumber} maxLength={11} inputMode="numeric" disabled={ro} className={show("carrierTaxNumber") ? "is-invalid" : ""} onChange={(e) => setTf("carrierTaxNumber", e.target.value.replace(/\D/g, ""))} />
                  {show("carrierTaxNumber") && <span className="po-fielderr">{fieldErrors.carrierTaxNumber}</span>}
                </label>
              </div>
              {show("driver") && <span className="po-fielderr">{fieldErrors.driver}</span>}
            </div>

            <div className="po-form-sec"><h4>Teslimat</h4>
              <div className="po-grid2">
                <label className="po-field"><span>Teslimat posta kodu<em>zorunlu</em></span>
                  <input value={t.deliveryPostalCode} maxLength={5} inputMode="numeric" placeholder="34200" disabled={ro} className={show("deliveryPostalCode") ? "is-invalid" : ""} onChange={(e) => setTf("deliveryPostalCode", e.target.value.replace(/\D/g, ""))} />
                  {show("deliveryPostalCode") && <span className="po-fielderr">{fieldErrors.deliveryPostalCode}</span>}
                </label>
                <div className="po-field"><span>Teslimat adresi</span>
                  <div className="sh-ed-addr">{[shipment.deliveryAddress, [shipment.deliveryDistrict, shipment.deliveryCity].filter(Boolean).join(" / ")].filter(Boolean).join(" · ") || "Adres girilmedi"}</div>
                </div>
              </div>
            </div>

            <div className="po-note info">
              {n} satır · {shFmtQty(shipment.totalQuantity)} birim · {action === "reissue" ? "stok yeniden düşülmez" : hasModule("stock") ? "üretim stoğu tanımlı modeller için stoktan çıkış işlenir" : "stok bakiyesi değişmez"}.
            </div>

            {canPaper && !paperOpen && (
              <button type="button" className="po-link sh-ed-paperlink" onClick={() => setPaperOpen(true)}>Entegratöre ulaşılamıyor mu? Matbu irsaliye ile sevk et</button>
            )}
            {canPaper && paperOpen && (
              <div className="po-form-sec sh-ed-paper"><h4>Matbu (kâğıt) irsaliye ile sevk</h4>
                <p className="po-form-hint">Yalnız teknik imkânsızlıkta: "e-İrsaliyeye Dönüştürülecektir" ibareli matbu irsaliye düzenleyin; en geç izleyen gün MATBUDAN e-İrsaliyeye dönüştürülür (Kılavuz §12).</p>
                <div className="po-grid2">
                  <label className="po-field"><span>Matbu irsaliye seri-sıra no<em>zorunlu</em></span>
                    <input value={printedNo} maxLength={32} className={show("printedWaybill") ? "is-invalid" : ""} onChange={(e) => { setPrintedNo(e.target.value); setServerErrors({}); }} />
                    {show("printedWaybill") && <span className="po-fielderr">{fieldErrors.printedWaybill}</span>}
                  </label>
                  <label className="po-field"><span>Matbu irsaliye tarihi</span><input type="date" value={printedDate} max={today()} onChange={(e) => setPrintedDate(e.target.value)} /></label>
                </div>
                <div className="po-actrow"><button type="button" className="po-btn danger" disabled={saving} onClick={runPaper}>{saving ? "Kaydediliyor" : "Matbu irsaliyeyle sevk et"}</button></div>
              </div>
            )}
          </>}

          {action === "deliver" && (
            <div className="po-grid2">
              <label className="po-field"><span>Teslim alan<em>zorunlu</em></span><input value={receivedBy} maxLength={100} ref={setFirst} placeholder="Ad soyad / kaşe" onChange={(e) => setReceivedBy(e.target.value)} /></label>
              <label className="po-field"><span>Teslim zamanı</span><input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></label>
            </div>
          )}

          {action === "cancel" && <>
            <label className="po-field">
              <span>İptal sebebi<em>zorunlu, en az 3 karakter</em></span>
              <textarea rows={3} maxLength={300} ref={setFirst} value={reason} placeholder="Örn. müşteri teslimatı erteledi" onChange={(e) => setReason(e.target.value)} />
            </label>
            <span className="po-counter">{reason.length}/300</span>
            <div className="po-note warn">
              {shipment.lines.some((l) => l.hasStockMovement)
                ? "Sevk ile üretilen stok çıkışları geri alınır; kayıt iptal olarak kalır."
                : shipment.status === S.Draft
                  ? "Taslak iptal olarak kalır; kalıcı olarak kaldırmak için Sil kullanın."
                  : "Kayıt iptal olarak kalır; sipariş miktarı yeniden sevk edilebilir."}
            </div>
          </>}
        </div>

        <div className="po-modal-foot between">
          <span className="po-hint"><b>{n}</b> satır · <b>{shFmtQty(shipment.totalQuantity)}</b> birim</span>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
            <button type="submit" className={`po-btn primary${action === "cancel" ? " danger" : ""}${despatchMode ? " sh-ed-submit" : ""}`} disabled={saving || (!despatchMode && !valid)}>
              {saving ? BUSY[action] : action === "ship" && despatchMode ? (outcome === "Unknown" ? "Yeniden dene" : "e-İrsaliye düzenle ve sevk et") : TITLE[action]}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ShipmentActionModal;
